import React, { useEffect, useState, useCallback } from 'react';
import { Button, message, Card, Tabs, List, Row, Col, DatePicker, Space, Input as AntInput, Collapse } from 'antd';
import { ArrowLeftOutlined, ClockCircleOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { OfferMerchant, MerchantDetail, Slot, Lead, AddSlotsRequest, AddSlotDTO } from '../types/auth';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { Form, Input, TimePicker, Modal } from 'antd';

const { TabPane } = Tabs;

const WinterOfferDetail: React.FC = () => {
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<OfferMerchant | null>(null);
  const [merchantDetail, setMerchantDetail] = useState<MerchantDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAddSlotModalVisible, setIsAddSlotModalVisible] = useState(false);
  const [addSlotForm] = Form.useForm();
  const [isAddingSlots, setIsAddingSlots] = useState(false);
  const [slotsDateFilter, setSlotsDateFilter] = useState<Dayjs | null>(() => {
    return dayjs().startOf('day');
  });
  const [parentSearch, setParentSearch] = useState<string>('');
  const [slotSearches, setSlotSearches] = useState<Record<number, string>>({});

  const loadMerchant = useCallback(async () => {
    if (!merchantId) return;
    
    setLoading(true);
    try {
      const merchants = await apiService.getWinterOfferMerchants();
      const foundMerchant = merchants.find(m => m.id === parseInt(merchantId));
      if (!foundMerchant) {
        message.error('Merchant not found');
        navigate('/admin/winter/kids');
        return;
      }
      setMerchant(foundMerchant);
      
      const detail = await apiService.getWinterOfferMerchantDetail(foundMerchant.id);
      setMerchantDetail(detail);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view merchant details.');
        navigate('/admin/winter/kids');
      } else {
        message.error('Failed to load merchant details');
        navigate('/admin/winter/kids');
      }
    } finally {
      setLoading(false);
    }
  }, [merchantId, navigate]);

  useEffect(() => {
    loadMerchant();
  }, [loadMerchant]);

  const handleAddSlot = () => {
    addSlotForm.resetFields();
    setIsAddSlotModalVisible(true);
  };

  const handleAddSlotSubmit = async (values: any) => {
    if (!merchant) return;
    
    setIsAddingSlots(true);
    try {
      const slots: AddSlotDTO[] = values.slots.map((slot: any) => {
        const date = dayjs(slot.date);
        const timeFrom = dayjs(slot.timeFrom);
        const timeTo = dayjs(slot.timeTo);
        
        const from = date.hour(timeFrom.hour()).minute(timeFrom.minute()).second(0).millisecond(0);
        const to = date.hour(timeTo.hour()).minute(timeTo.minute()).second(0).millisecond(0);
        
        return {
          title: slot.title || undefined,
          from: from.toISOString(),
          to: to.toISOString()
        };
      });
      
      const request: AddSlotsRequest = { slots };
      await apiService.addWinterOfferSlots(merchant.id, request);
      message.success('Slots added successfully');
      setIsAddSlotModalVisible(false);
      addSlotForm.resetFields();
      
      await loadMerchant();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to add slots.');
      } else {
        message.error(error.response?.data?.msg || 'Failed to add slots');
      }
    } finally {
      setIsAddingSlots(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    return dayjs(dateTime).format('DD.MM.YYYY HH:mm');
  };

  const filteredSlots = (merchantDetail?.slots || []).filter(slot => {
    if (!slotsDateFilter) {
      return true;
    }
    const slotFrom = dayjs(slot.from);
    const filterDate = slotsDateFilter.startOf('day');
    return slotFrom.isSame(filterDate, 'day');
  });

  const getLeadsForSlot = (slotId: number): Lead[] => {
    if (!merchantDetail || !merchantDetail.leads) return [];
    const slotSearchText = slotSearches[slotId] || '';
    return merchantDetail.leads.filter(lead => {
      // Filter by slotId
      if (lead.slotId !== slotId) return false;
      // Filter by general parent search
      if (parentSearch && !lead.parent.toLowerCase().includes(parentSearch.toLowerCase())) {
        return false;
      }
      // Filter by slot-specific parent search
      if (slotSearchText && !lead.parent.toLowerCase().includes(slotSearchText.toLowerCase())) {
        return false;
      }
      return true;
    });
  };

  const handleSlotSearchChange = (slotId: number, value: string) => {
    setSlotSearches(prev => ({
      ...prev,
      [slotId]: value
    }));
  };

  if (loading || !merchant || !merchantDetail) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/winter/kids')}>
          Back to List
        </Button>
      </Space>

      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            {merchant.name}
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <strong>Description:</strong> {merchant.description}
          </Col>
          <Col span={6}>
            <strong>Capacity:</strong> {merchant.capacity}
          </Col>
          <Col span={6}>
            <strong>Age:</strong> {merchant.age}
          </Col>
        </Row>
      </Card>

      <Tabs 
        defaultActiveKey="slots"
        tabBarExtraContent={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSlot}>
            Add Time Slot
          </Button>
        }
      >
        <TabPane 
          tab={`Slots (${merchantDetail.slots.length})`} 
          key="slots"
        >
          <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <DatePicker
                format="DD.MM.YYYY"
                placeholder="Select date"
                value={slotsDateFilter}
                onChange={(date) => setSlotsDateFilter(date ? date.startOf('day') : null)}
                style={{ width: '100%' }}
              />
              <AntInput
                placeholder="Search by parent (all slots)"
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
                allowClear
                style={{ width: '100%' }}
              />
            </Space>
          </div>
          <List
            dataSource={filteredSlots}
            renderItem={(slot: Slot) => {
              const slotTitle = slot.title 
                ? slot.title 
                : `${formatDateTime(slot.from)} - ${formatDateTime(slot.to)}`;
              const slotLeads = getLeadsForSlot(slot.id);
              return (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <List.Item.Meta
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ClockCircleOutlined />
                          {slotTitle}
                        </div>
                      }
                      description={
                        slot.title ? (
                          <div>
                            <div><strong>From:</strong> {formatDateTime(slot.from)}</div>
                            <div><strong>To:</strong> {formatDateTime(slot.to)}</div>
                          </div>
                        ) : null
                      }
                    />
                    {slotLeads.length > 0 && (
                      <div style={{ marginTop: 16, paddingLeft: 24 }}>
                        <Collapse
                          size="small"
                          items={[
                            {
                              key: `leads-${slot.id}`,
                              label: `Leads (${slotLeads.length})`,
                              children: (
                                <div>
                                  <AntInput
                                    placeholder="Search by parent"
                                    value={slotSearches[slot.id] || ''}
                                    onChange={(e) => handleSlotSearchChange(slot.id, e.target.value)}
                                    allowClear
                                    style={{ width: '100%', marginBottom: 12 }}
                                  />
                                  <List
                                    size="small"
                                    dataSource={slotLeads}
                                    renderItem={(lead: Lead) => (
                                      <List.Item>
                                        <div>
                                          <div><strong>Parent:</strong> {lead.parent}</div>
                                          <div><strong>Phone:</strong> {lead.phone}</div>
                                          <div><strong>Age:</strong> {lead.age ?? '-'}</div>
                                          {lead.createdAt && (
                                            <div><strong>Created:</strong> {formatDateTime(lead.createdAt)}</div>
                                          )}
                                        </div>
                                      </List.Item>
                                    )}
                                    locale={{ emptyText: 'No leads' }}
                                  />
                                </div>
                              )
                            }
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
            locale={{ emptyText: 'No slots found' }}
          />
        </TabPane>
      </Tabs>

      {/* Add Slot Modal */}
      <Modal
        title="Add Time Slots"
        open={isAddSlotModalVisible}
        onCancel={() => {
          setIsAddSlotModalVisible(false);
          addSlotForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={addSlotForm}
          layout="vertical"
          onFinish={handleAddSlotSubmit}
          initialValues={{
            slots: [{ title: '', date: null, timeFrom: null, timeTo: null }]
          }}
        >
          <Form.List name="slots">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    title={`Slot ${name + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          danger
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      ) : null
                    }
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      label="Title (Optional)"
                    >
                      <Input placeholder="Enter slot title" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'date']}
                      label="Date"
                      rules={[{ required: true, message: 'Please select date' }]}
                    >
                      <DatePicker
                        format="DD.MM.YYYY"
                        style={{ width: '100%' }}
                        placeholder="Select date"
                      />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'timeFrom']}
                          label="Time From"
                          rules={[{ required: true, message: 'Please select start time' }]}
                        >
                          <TimePicker
                            format="HH:mm"
                            style={{ width: '100%' }}
                            placeholder="Select start time"
                            onChange={(time) => {
                              if (time) {
                                const timeTo = time.clone().add(1, 'hour');
                                addSlotForm.setFieldValue(['slots', name, 'timeTo'], timeTo);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'timeTo']}
                          label="Time To"
                          rules={[{ required: true, message: 'Please select end time' }]}
                        >
                          <TimePicker
                            format="HH:mm"
                            style={{ width: '100%' }}
                            placeholder="Select end time"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Another Slot
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={isAddingSlots}
              >
                Add Slots
              </Button>
              <Button
                onClick={() => {
                  setIsAddSlotModalVisible(false);
                  addSlotForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WinterOfferDetail;
